// Never react-router's useNavigate here: this app carries tenant-handle + club-id in the query
// string, and a bare path drops them — MissingParamsGuard then blocks the whole app.
import { TenantLink, useTenantNavigate, useTenantSearchParams } from '~/lib/navigation';

/**
 * 404 Not Found Page
 *
 * Shows when a route doesn't match any defined paths.
 * Provides helpful navigation options to get back on track.
 */
export function NotFoundPage() {
  // Preserves tenant-handle + club-id on every destination, so this page no longer needs the
  // hand-rolled buildUrl() it used to carry.
  const navigate = useTenantNavigate();
  const [searchParams] = useTenantSearchParams();

  // Still read directly: the "Make a Booking" action only makes sense once we know which club.
  const tenantHandle = searchParams.get('tenant-handle');
  const clubId = searchParams.get('club-id');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-card border border-border rounded-lg shadow-lg p-8 text-center">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
            <svg
              className="w-12 h-12 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Header */}
        <h1 className="text-4xl font-bold text-foreground mb-4">
          404
        </h1>
        <h2 className="text-2xl font-semibold text-foreground mb-4">
          Page Not Found
        </h2>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          The page you're looking for doesn't exist. It may have been moved or removed, or you may have mistyped the URL.
        </p>

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-6 py-3 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm font-medium transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Go Back
          </button>
          
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Go to Home
          </TenantLink>

          {tenantHandle && clubId && (
            <TenantLink
              to="/"
              className="inline-flex items-center px-6 py-3 border border-border bg-background rounded-md hover:bg-accent text-sm font-medium transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Make a Booking
            </TenantLink>
          )}
        </div>

        {/* Dev Info */}
        {import.meta.env.DEV && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground mb-2">
              Debug Info (Dev Only)
            </summary>
            <div className="bg-muted rounded-md p-4 text-xs font-mono">
              <div className="space-y-1">
                <div>
                  <span className="text-muted-foreground">Current URL:</span>{' '}
                  <span className="text-foreground">{window.location.href}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Path:</span>{' '}
                  <span className="text-foreground">{window.location.pathname}</span>
                </div>
                {tenantHandle && (
                  <div>
                    <span className="text-muted-foreground">Tenant:</span>{' '}
                    <span className="text-foreground">{tenantHandle}</span>
                  </div>
                )}
                {clubId && (
                  <div>
                    <span className="text-muted-foreground">Club ID:</span>{' '}
                    <span className="text-foreground">{clubId}</span>
                  </div>
                )}
              </div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
